import { Box, Flex, Text } from '@mantine/core'
import { Link, RichTextEditor as MantineRTE } from '@mantine/tiptap'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { clsx } from 'clsx'
import React from 'react'

import { type RichTextEditorProps } from './RichTextEditor.types'
import styles from './styles.module.css'

export const RichTextEditor = ({
  required,
  error,
  label,
  description,
  onChange,
  value = '',
  placeholder = '',
  onBlur,
  readOnly = false,
  updateIfChanged = false,
  withToolbar = true,
  wrapperContainerId = 'rich-text-editor-wrapper',
  disabled = false,
}: RichTextEditorProps) => {
  const [content, setContent] = React.useState(value)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        HTMLAttributes: {
          class: styles.link,
        },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: false,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      const value = editor.isEmpty ? '' : editor.getHTML()
      setContent(value)
    },
    onBlur: ({ editor }) => {
      const value = editor.isEmpty ? '' : editor.getHTML()
      onBlur?.(value)
    },
  })

  React.useEffect(() => {
    if (!editor) return
    editor.setOptions({
      editable: !readOnly,
    })
  }, [readOnly, editor])

  React.useEffect(() => {
    if (editor) {
      onChange?.(editor.getHTML())
    }
  }, [content])

  React.useEffect(() => {
    if (content === value) return

    editor?.chain().setContent(value).run()
  }, [value, updateIfChanged])

  return (
    <React.Suspense>
      {label && (
        <Flex mb='sm' gap='sm'>
          <Text fw={500}>{label}</Text>
          {required && <Text c='red.6'>*</Text>}
        </Flex>
      )}

      <Box
        id={wrapperContainerId}
        className={clsx(styles.textEditor, {
          [styles.readOnly]: readOnly,
          [styles.withError]: error,
          [styles.withToolbar]: withToolbar,
          [styles.disabled]: disabled,
        })}
      >
        <MantineRTE editor={editor}>
          {withToolbar && (
            <MantineRTE.Toolbar>
              <MantineRTE.ControlsGroup>
                <MantineRTE.Bold />
                <MantineRTE.Italic />
                <MantineRTE.Underline />
                <MantineRTE.Strikethrough />
                <MantineRTE.ClearFormatting />
              </MantineRTE.ControlsGroup>
              <MantineRTE.ControlsGroup>
                <MantineRTE.Link />
                <MantineRTE.Unlink />
              </MantineRTE.ControlsGroup>
            </MantineRTE.Toolbar>
          )}

          <MantineRTE.Content />
        </MantineRTE>
      </Box>

      {!error && description && (
        <Text size='sm' mt='xs' c='dimmed'>
          {description}
        </Text>
      )}

      {error && (
        <Text size='sm' mt='xs' c='red.6'>
          {error}
        </Text>
      )}
    </React.Suspense>
  )
}
